import React, { useState, useEffect } from 'react';
import { 
  UserIcon, 
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAuth } from '../../hooks/useAuth';
import { useApi } from '../../hooks/useApi';
import { API_ENDPOINTS } from '../../constants/api';
import useNotification from '../../hooks/useNotification';

const getInitials = (user) => {
  if (!user) return '';
  const first = user.firstName ? user.firstName[0] : '';
  const last = user.lastName ? user.lastName[0] : '';
  return (first + last).toUpperCase();
};

const UserCard = () => {
  const { user, updateUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    middleName: user?.middleName || '',
    position: user?.position || ''
  });

  const { putData: updateUserData } = useApi(API_ENDPOINTS.UPDATE_PROFILE);

  useEffect(() => {
    setForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      middleName: user?.middleName || '',
      position: user?.position || ''
    });
  }, [user]);

  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const response = await updateUserData(form);
      // Сервер возвращает { message, user }
      updateUser(response.user);
      setIsEditing(false);
      showSuccess('Профиль успешно обновлен');
    } catch (error) {
      console.error('Update error:', error);
      showError(error.response?.data?.descr || 'Ошибка при обновлении профиля');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      middleName: user?.middleName || '',
      position: user?.position || ''
    });
    setIsEditing(false);
  };

  const getRoleDisplay = (roles) => {
    if (!roles || !Array.isArray(roles)) return 'Пользователь';
    
    if (roles.includes('ADMIN')) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ShieldCheckIcon className="w-3 h-3 mr-1" />
          Администратор
        </span>
      );
    }
    
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <UserIcon className="w-3 h-3 mr-1" />
        Пользователь
      </span>
    );
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Загрузка профиля...</div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-10 flex flex-col gap-8 relative">
        {/* Кнопка редактирования */}
          {!isEditing && (
            <Button
              variant="outline"
              onClick={() => setIsEditing(true)}
            className="absolute top-6 right-6 flex items-center border-gray-300 hover:border-gray-400"
            >
              <PencilIcon className="w-4 h-4 mr-2" />
              Редактировать
            </Button>
          )}
        {/* Аватар */}
        <div className="flex flex-col items-center mb-6">
          <div className="w-24 h-24 rounded-full bg-gradient-to-tr from-blue-200 to-blue-400 flex items-center justify-center text-3xl font-bold text-white shadow-md border-4 border-white">
            {getInitials(user) || <UserIcon className="w-12 h-12 text-white opacity-70" />}
          </div>
              </div>
        {/* Таблица профиля: две равные колонки */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <tbody className="bg-white">
              <tr>
                <td className="py-3 pr-4 font-medium text-gray-500 text-left w-1/2">Имя</td>
                <td className="py-3 text-left w-1/2">
              {isEditing ? (
                <Input
                  value={form.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="Введите имя"
                      className="w-full"
                />
              ) : (
                    <span className="text-gray-900 font-semibold">{user.firstName || 'Не указано'}</span>
              )}
                </td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-gray-500 text-left w-1/2">Фамилия</td>
                <td className="py-3 text-left w-1/2">
              {isEditing ? (
                <Input
                  value={form.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Введите фамилию"
                      className="w-full"
                />
              ) : (
                    <span className="text-gray-900 font-semibold">{user.lastName || 'Не указано'}</span>
              )}
                </td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-gray-500 text-left w-1/2">Отчество</td>
                <td className="py-3 text-left w-1/2">
              {isEditing ? (
                <Input
                  value={form.middleName}
                  onChange={(e) => handleInputChange('middleName', e.target.value)}
                  placeholder="Введите отчество"
                      className="w-full"
                />
              ) : (
                    <span className="text-gray-900 font-semibold">{user.middleName || 'Не указано'}</span>
              )}
                </td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-gray-500 text-left w-1/2">Должность</td>
                <td className="py-3 text-left w-1/2">
              {isEditing ? (
                <Input
                  value={form.position}
                  onChange={(e) => handleInputChange('position', e.target.value)}
                  placeholder="Введите должность"
                      className="w-full"
                />
              ) : (
                    <span className="text-gray-900 font-semibold">{user.position || 'Не указано'}</span>
              )}
                </td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-gray-500 text-left w-1/2">Email</td>
                <td className="py-3 text-left w-1/2">
                  <span className="text-gray-900 font-semibold">{user.email}</span>
                </td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-medium text-gray-500 text-left w-1/2">Роль</td>
                <td className="py-3 text-left w-1/2">
                  {getRoleDisplay(user.roles)}
                </td>
              </tr>
            </tbody>
          </table>
            </div>
        {/* Кнопки сохранения/отмены */}
          {isEditing && (
          <div className="flex justify-end gap-4 pt-2">
              <Button
                variant="primary"
                onClick={handleSave}
                loading={isLoading}
              className="flex items-center px-6"
              >
                <CheckIcon className="w-4 h-4 mr-2" />
                Сохранить
              </Button>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              className="flex items-center px-6"
              >
                <XMarkIcon className="w-4 h-4 mr-2" />
                Отмена
              </Button>
            </div>
          )}
      </div>
    </div>
  );
};

export default UserCard; 